import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database'; // Realtime Database imports
import useProcessData from '../../contexts/useProcessData';
import Swal from 'sweetalert2';
import { urls } from '../../services/apiService';
import { User } from 'lucide-react';

const UploadImg = ({ setOpenImgSelecter }) => {
  const [error, setError] = useState(null);
  const [deviceId, setDeviceId] = useState('');
  const [devices, setDevices] = useState([]); // State to store device IDs from API
  const [availableDates, setAvailableDates] = useState([]); // State to store available dates from Realtime Database
  const [selectedDates, setSelectedDates] = useState(null); // State to store selected dates
  const [user, setUser] = useState(null); // State to store authenticated user
  const [isLoadingDates, setIsLoadingDates] = useState(false); // State to track loading status
  const { processData, dataLoading, dataError, resultStatus } = useProcessData();

  // Initialize Firebase Auth
  const auth = getAuth();

  // Check if the user is authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user); // Set the authenticated user
      } else {
        setUser(null);
        setError('User must be authenticated to access this feature.');
      }
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, [auth]);

  // Fetch device IDs from the API
  useEffect(() => {
    const fetchDeviceIds = async () => {
      try {
        const response = await fetch(urls.get_device_id); // Fetch device IDs from API
        
        if (!response.ok) {
          throw new Error('Failed to fetch device IDs');
        }
        const data = await response.json();
       
        const userEmail = localStorage.getItem('User'); // Adjust key if it's different

        if (data && data.devices && userEmail) {
          // Filter devices where the second item (index 1) matches the logged-in user email
          const filteredDevices = data.devices.filter(device => device[1] === userEmail);
          setDevices(filteredDevices); // Set only the matched devices
          localStorage.setItem('userDevices', JSON.stringify(filteredDevices)); // Store in localStorage
        }

      } catch (error) {
        console.error('Error fetching device IDs:', error);
        setError('Failed to fetch device IDs');
      }
    };
    fetchDeviceIds();
  }, [User]);

  // Fetch available dates from Realtime Database when deviceId changes
  useEffect(() => {
    if (!deviceId) return; // Exit if no device ID is selected

    const fetchAvailableDates = async () => {
      setIsLoadingDates(true); // Set loading to true
      try {
        const db = getDatabase();
        const deviceRef = ref(db, `${deviceId}`); // Reference to specific deviceId
        const snapshot = await get(deviceRef);

        if (snapshot.exists()) {
          const deviceData = snapshot.val();
      

          // Extract timestamps and convert to unique dates (YYYY-MM-DD)
          const uniqueDates = [...new Set(
            Object.keys(deviceData).map(timestamp => timestamp.split('-').slice(0, 3).join('-')) // Extract YYYY-MM-DD
          )];


          setAvailableDates(uniqueDates);
        
        } else {
          setAvailableDates([]); // No data found
            console.log("to see: ",availableDates)
        }
      } catch (error) {
        console.error('Error fetching available dates:', error);
        setError('Failed to fetch available dates');
      } finally {
        setIsLoadingDates(false); // Set loading to false
      }
    };

    fetchAvailableDates();
  }, [deviceId]); // Runs when deviceId changes



  const handleDataSubmit = () => {
    if (!selectedDates) {
      setError('Please select a date');
      return;
    }
    processData(deviceId, selectedDates);

  };
  useEffect(() => {
    if (resultStatus == 200) {
      Swal.fire({
        title: "Success!",
        text: "Heatmap generated successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });
      setOpenImgSelecter(false);
    } else if (resultStatus == 400) {
      Swal.fire({
        title: "Error!",
        text: "Not enough valid points to generate heatmap, Please! try again with different dates",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }, [resultStatus])


  return (
    <div className="absolute w-full h-screen bg-black bg-opacity-50 top-0 left-0 right-0 z-[500] flex items-center justify-center">
      <div className="relative w-[90%] md:w-[80%] lg:w-[70%] min-h-[400px] bg-white flex flex-col items-center justify-start p-4 rounded-xl">
        <span
          className="absolute right-2 top-2 text-xl cursor-pointer hover:scale-105 duration-300 bg-gray-300 rounded-full p-2"
          onClick={() => setOpenImgSelecter(false)}
        >
          <FaTimes />
        </span>

        <div className="mt-5 w-[70%]">
          <p className="text-2xl font-semibold">Select Your Device ID </p>
          <p className="text-sm mt-1 font-medium text-gray-500">
            Here, you can select your device ID, which will be fetched based on the username it is registered to. The maps generated will be for the dates associated with that device, but they must contain valid data points.
          </p>
        </div>

        <div className="bg-gray-200 p-3 w-[70%] mt-4 rounded-xl">
          <div className="flex flex-col space-y-4">
            {/* Device ID Dropdown */}
            <div>
              <label htmlFor="deviceId" className="block text-sm font-medium text-gray-700">Device ID</label>
              <select
                id="deviceId"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
              >
                <option value="">Select Device ID</option>
                {devices.map((device, index) => (
                  <option key={index} value={device[2]}> {/* device[2] is the device ID */}
                    {device[2]} {/* Display the device ID */}
                  </option>
                ))}
              </select>
            </div>

            {/* Display Available Dates */}
            {deviceId && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Available Dates</label>
                {isLoadingDates ? (
                  <p className='text-gray-600 animate-pulse' >Loading dates...</p> // Display loader or loading text
                ) : (
                  <select
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    onChange={(e) => setSelectedDates(e.target.value)}
                  >
                    <option value="">Select a Date</option>
                    {availableDates.map((date, index) => (
                      <option key={index} value={date}>
                        {date}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Buttons for Cancel and Save on device id  */}
        {(deviceId && selectedDates) && (
          <div className="flex items-center justify-between w-[50%] mt-4 bg-[#333] p-2 rounded-md gap-2">
            <button
              onClick={() => setOpenImgSelecter(false)}
              className="w-1/2 py-3 rounded bg-[#474747] text-white border-none outline-none cursor-pointer hover:bg-black duration-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleDataSubmit}
              className="w-1/2 py-3 rounded bg-green-500 text-white border-none outline-none cursor-pointer hover:bg-green-600 duration-300 hover:text-white"
            >
              {dataLoading ? "Loading..." : "Generate"}
            </button>
          </div>
        )}


      </div>
    </div>
  );
};

export default UploadImg;